/**
 * VersionHistory — timeline of rule changes with diff view and rollback.
 * Fetches from GET /api/v1/rules/{id}/history.
 */
import { useState, useEffect } from 'react';
import { API_V1, authFetch } from '../../constants';

interface HistoryEntry {
  id: number;
  version: number;
  change_type: string;
  old_conditions: any;
  new_conditions: any;
  changed_by: number;
  changed_at: string;
}

interface VersionHistoryProps {
  ruleId: number;
  currentVersion: number;
  onRevert: (conditions: any) => void;
  btnStyle: React.CSSProperties;
}

const CHANGE_LABELS: Record<string, string> = {
  CREATED: 'Initial creation',
  MODIFIED: 'Conditions modified',
  ACTIVATED: 'Activated',
  DEACTIVATED: 'Deactivated',
  DELETED: 'Deleted',
};

export function VersionHistory({ ruleId, currentVersion, onRevert, btnStyle }: VersionHistoryProps) {
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedDiff, setExpandedDiff] = useState<number | null>(null);

  useEffect(() => {
    setLoading(true);
    authFetch(`${API_V1}/rules/${ruleId}/history`)
      .then(r => r.ok ? r.json() : { history: [] })
      .then(data => {
        setHistory(data?.history || data || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [ruleId]);

  if (loading) {
    return (
      <div className="card" style={{ padding: 20, marginBottom: 16 }}>
        <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Version History</h4>
        <p style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Loading...</p>
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="card" style={{ padding: 20, marginBottom: 16 }}>
        <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Version History</h4>
        <p style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>No version history available.</p>
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: 20, marginBottom: 16 }}>
      <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Version History</h4>

      <div style={{ position: 'relative', paddingLeft: 20 }}>
        {/* Timeline line */}
        <div style={{
          position: 'absolute', left: 5, top: 0, bottom: 0,
          width: 2, background: 'var(--border-default)',
        }} />

        {history.map((entry, idx) => {
          const isCurrent = entry.version === currentVersion;
          const hasDiff = entry.old_conditions || entry.new_conditions;
          const isExpanded = expandedDiff === entry.id;

          return (
            <div key={entry.id} style={{ position: 'relative', marginBottom: 16, paddingBottom: 4 }}>
              {/* Timeline dot */}
              <div style={{
                position: 'absolute', left: -18, top: 4,
                width: 8, height: 8, borderRadius: '50%',
                background: isCurrent ? 'var(--accent, var(--accent))' : 'var(--text-tertiary)',
                border: '2px solid var(--bg-primary, #0a0f1a)',
              }} />

              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: isCurrent ? 'var(--accent, var(--accent))' : 'inherit' }}>
                  v{entry.version}
                  {isCurrent && <span style={{ fontSize: 10, marginLeft: 6, color: 'var(--accent)' }}>(current)</span>}
                </span>
                <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                  {new Date(entry.changed_at).toLocaleDateString()}
                </span>
              </div>

              {/* Change description */}
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>
                {CHANGE_LABELS[entry.change_type] || entry.change_type}
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: 8 }}>
                {hasDiff && (
                  <button
                    onClick={() => setExpandedDiff(isExpanded ? null : entry.id)}
                    style={{ ...btnStyle, fontSize: 10, padding: '2px 8px' }}
                  >
                    {isExpanded ? 'Hide Diff' : 'View Diff'}
                  </button>
                )}
                {!isCurrent && entry.new_conditions && (
                  <button
                    onClick={() => onRevert(entry.new_conditions)}
                    style={{ ...btnStyle, fontSize: 10, padding: '2px 8px', color: 'var(--amber)' }}
                  >
                    Revert to v{entry.version}
                  </button>
                )}
              </div>

              {/* Diff view */}
              {isExpanded && hasDiff && (
                <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginBottom: 4, textTransform: 'uppercase' }}>
                      Before
                    </div>
                    <pre style={{
                      fontSize: 11, padding: 10, borderRadius: 6,
                      background: 'rgba(239,68,68,0.06)', border: '1px solid #fef2f2',
                      color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', overflow: 'auto',
                      maxHeight: 200,
                    }}>
                      {entry.old_conditions ? JSON.stringify(entry.old_conditions, null, 2) : '(none)'}
                    </pre>
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginBottom: 4, textTransform: 'uppercase' }}>
                      After
                    </div>
                    <pre style={{
                      fontSize: 11, padding: 10, borderRadius: 6,
                      background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)',
                      color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', overflow: 'auto',
                      maxHeight: 200,
                    }}>
                      {entry.new_conditions ? JSON.stringify(entry.new_conditions, null, 2) : '(none)'}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
